import { motion } from "framer-motion";
import { ExternalLink, Store } from "lucide-react";
import { useData } from "@/i18n/LangContext";

interface MetaInfoProps {
  title: string;
  tags: string[];
  link?: string;
  appStoreUrl?: string;
  websiteUrl?: string;
  projectLinks?: Array<{ label: string; url: string; linkLabel?: string }>;
  links?: Array<{ label: string; url: string }>;
}


// Erzwingt den Umbruch direkt NACH dem Gedankenstrich: „Schnack —" / „Privater Messenger"
const breakAfterDash = (text: string) => {
  const parts = text.split(" — ");
  if (parts.length === 1) return text;
  return parts.map((part, i) => (
    <span key={i}>
      {part}
      {i < parts.length - 1 && (<> —<br /></>)}
    </span>
  ));
};

export default function MetaInfo({ title, tags, appStoreUrl, websiteUrl, projectLinks, links }: MetaInfoProps) {
  const { ui } = useData();
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  return (
    <motion.div
      className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20"
      variants={container}
      initial="hidden"
      animate="show"
      data-cy="meta-info"
    >
      <motion.div variants={item} className="backdrop-blur-sm bg-[rgba(30,12,50,0.2)] p-6 rounded-xl border border-[rgba(255,136,255,0.15)] shadow-glow-sm">
        <h3 className="text-[#ff88ff] text-sm font-medium mb-3 uppercase tracking-wider">{ui.workDetail.project}</h3>
        <p className="text-white text-xl font-medium">{breakAfterDash(title)}</p>
        {projectLinks?.map(pl => (
          <p key={pl.label} className="text-white text-xl font-medium mt-2">{breakAfterDash(pl.label)}</p>
        ))}
      </motion.div>

      <motion.div variants={item} className="backdrop-blur-sm bg-[rgba(30,12,50,0.2)] p-6 rounded-xl border border-[rgba(255,136,255,0.15)] shadow-glow-sm">
        <h3 className="text-[#ff88ff] text-sm font-medium mb-3 uppercase tracking-wider">{ui.workDetail.technologies}</h3>
        <div className="flex flex-wrap gap-2">
          {tags.map(tag => (
            <span
              key={tag}
              className="inline-block bg-[#1E0B32] border border-[#ff88ff]/20 px-2 py-1 rounded text-white/80 text-sm"
            >
              {tag}
            </span>
          ))}
        </div>
      </motion.div>

      {(appStoreUrl || websiteUrl || (projectLinks && projectLinks.length > 0) || (links && links.length > 0)) && (
        <motion.div variants={item} className="backdrop-blur-sm bg-[rgba(30,12,50,0.2)] p-6 rounded-xl border border-[rgba(255,136,255,0.15)] shadow-glow-sm">
          <h3 className="text-[#ff88ff] text-sm font-medium mb-3 uppercase tracking-wider">{ui.workDetail.links}</h3>
          <div className="flex flex-col gap-3">
            {appStoreUrl && (
              <a
                href={appStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#ff88ff] text-[#1E0B32]! rounded-full text-sm font-semibold hover:bg-[#ff88ff]/85 transition-colors self-start"
              >
                <Store size={16} strokeWidth={2} />
                {ui.workDetail.appStoreDownload}
              </a>
            )}
            {websiteUrl && (
              <a
                href={websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 border border-[#ff88ff]/40 text-[#ff88ff] rounded-full text-sm font-semibold hover:border-[#ff88ff] transition-colors self-start"
              >
                <ExternalLink size={16} strokeWidth={2} />
                Website
              </a>
            )}
            {projectLinks?.map(pl => (
              <a
                key={pl.url}
                href={pl.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 border border-[#ff88ff]/40 text-[#ff88ff] rounded-full text-sm font-semibold hover:border-[#ff88ff] transition-colors self-start"
              >
                <ExternalLink size={16} strokeWidth={2} />
                {pl.linkLabel || pl.label}
              </a>
            ))}
            {links?.map(l => (
              <a
                key={l.url}
                href={l.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 border border-[#ff88ff]/40 text-[#ff88ff] rounded-full text-sm font-semibold hover:border-[#ff88ff] transition-colors self-start"
              >
                <ExternalLink size={16} strokeWidth={2} />
                {l.label}
              </a>
            ))}
          </div>
        </motion.div>
      )}

    </motion.div>
  );
}